import { useTranslation } from 'react-i18next';
import { TextField } from '@mui/material';

import type { RuteroDeclarablePassengerField } from '@bandido/shared/types';

import {
  PASSENGER_FIELD_INPUT,
  PASSENGER_FIELD_MAX_LENGTH,
  passengerFieldValueTooLong,
} from '../passengerFields';
import type { PassengerFieldLevel } from '../passengerFields';
import { CountrySelect } from './CountrySelect';
import { PassengerFieldSlot } from './PassengerFieldSlot';
import { PhoneField } from './PhoneField';
import { TransferIdentityDocumentControl } from './TransferIdentityDocumentControl';
import type { IdentityDocumentControlContext } from './TransferIdentityDocumentControl';

interface Props {
  field: RuteroDeclarablePassengerField;
  /**
   * Lo declara la OFERTA, no el vertical, y dice QUÉ decisión condiciona el
   * dato: `hold` y `confirm` se completan antes de reservar porque el confirm
   * no vuelve a transportar pasajeros; `quote` obtiene el precio de la sesión;
   * un `recommended` no bloquea nunca.
   */
  level: PassengerFieldLevel;
  /** El proveedor lo señaló como faltante al rechazar el hold. */
  error?: boolean;
  value?: string;
  onChange: (value: string) => void;
  /** País con el que arranca un control de país: el de la agencia (#2374). */
  defaultCountry?: string;
  disabled?: boolean;
  identityDocument?: IdentityDocumentControlContext;
}

/**
 * Un campo de pasajero que la oferta declaró. El control sale del campo (fecha,
 * país, texto) y no del vertical: así los 9 verticales piden lo suyo con la
 * misma pantalla y agregar un campo al vocabulario no toca ningún formulario.
 *
 * El asterisco de MUI marca lo que el asesor debe completar antes de reservar.
 * El badge conserva el momento contractual en que lo valida el proveedor.
 */
export function PassengerFieldControl({
  field,
  level,
  error,
  value,
  onChange,
  defaultCountry,
  disabled,
  identityDocument,
}: Props) {
  const { t } = useTranslation();
  const label = t(`travelServices.booking.paxFields.${field}`);
  const maxLength = PASSENGER_FIELD_MAX_LENGTH[field];
  const required =
    level === 'hold' || level === 'confirm' || level === 'quote';
  /** Solo el multilínea puede excederse: los demás recortan en el input. */
  const tooLong = passengerFieldValueTooLong(value, field);

  if (field === 'passportNumber' && identityDocument) {
    return <PassengerFieldSlot level={level}>
      <TransferIdentityDocumentControl {...identityDocument} required={required} error={error} disabled={disabled} />
    </PassengerFieldSlot>;
  }

  const control =
    PASSENGER_FIELD_INPUT[field] === 'phone' ? (
      <PhoneField
        label={label}
        required={required}
        error={error}
        value={value}
        onChange={onChange}
        defaultCountry={defaultCountry}
        disabled={disabled}
      />
    ) : PASSENGER_FIELD_INPUT[field] === 'country' ? (
      <CountrySelect
        fullWidth
        label={label}
        required={required}
        error={error}
        value={value}
        onChange={onChange}
        disabled={disabled}
      />
    ) : PASSENGER_FIELD_INPUT[field] === 'multiline' ? (
      // Texto libre para el operador. SIN `maxLength` en el input a propósito:
      // el browser recorta un paste EN SILENCIO, y el contrato del hub responde
      // 400 en vez de truncar justamente porque cortar una instrucción al
      // conductor puede cambiarle el sentido. El exceso se marca como error de
      // campo —la misma expresión que bloquea el CTA— y lo acorta el asesor.
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={2}
        required={required}
        error={error || tooLong}
        label={label}
        placeholder={t(`travelServices.booking.paxFieldPlaceholders.${field}`, {
          defaultValue: '',
        })}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        helperText={
          tooLong
            ? t('travelServices.booking.paxFieldTooLong', {
              max: maxLength,
              count: value?.length ?? 0,
            })
            : undefined
        }
        disabled={disabled}
      />
    ) : (
      <TextField
        size="small"
        fullWidth
        required={required}
        error={error}
        label={label}
        type={PASSENGER_FIELD_INPUT[field] === 'date' ? 'date' : 'text'}
        value={value ?? ''}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        {...(PASSENGER_FIELD_INPUT[field] === 'date'
          ? { InputLabelProps: { shrink: true } }
          : {})}
        {...(maxLength ? { inputProps: { maxLength } } : {})}
      />
    );

  return <PassengerFieldSlot level={level}>{control}</PassengerFieldSlot>;
}
